import { useAppSelector } from '@/store/store-hooks'
import { selectCurrentFolderBreadcrumbs, selectActiveRootFolderId } from '../store/selectors'
import { selectMatterById } from '@/matter/store/selectors'
import { matterPaths } from '@/routing/paths'
import { useNavigate, useParams } from 'react-router-dom'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

/**
 * FolderBreadcrumbs
 *
 * Navigation path from root to current folder.
 * Clicking a breadcrumb navigates to that folder.
 *
 * Navigation is user-action-driven (not effect-driven) to ensure clean browser history.
 * The URL change triggers the URL → Redux sync effect in UserFilesFolderRouteHandler.
 */
export function FolderBreadcrumbs({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()
  const breadcrumbs = useAppSelector(selectCurrentFolderBreadcrumbs)
  const rootFolderId = useAppSelector(selectActiveRootFolderId)
  const matter = useAppSelector((state) => selectMatterById(state, { matterId: matterId ?? null }))
  const matterName = matter?.name ?? 'Files'

  const handleNavigate = (folderId: string) => {
    if (!matterId) return

    onNavigate?.()
    // Navigate directly - URL change will trigger Redux sync
    const isRoot = folderId === rootFolderId
    if (isRoot) {
      navigate(matterPaths.userFiles.root(matterId))
    } else {
      navigate(matterPaths.userFiles.folder(matterId, folderId))
    }
  }

  if (breadcrumbs.length === 0) {
    return <nav className="text-sm text-brand-neutral-500">{matterName}</nav>
  }

  const lastIndex = breadcrumbs.length - 1

  const breadcrumbItems: BreadcrumbItemType[] = breadcrumbs.map((folder, index) => {
    const displayName = folder.is_root ? matterName : folder.name
    return {
      id: folder.id,
      label: displayName,
      onClick: index === lastIndex ? undefined : () => handleNavigate(folder.id),
    }
  })

  return <Breadcrumbs maxItems={3} maxLabelLength={20} maxActiveLabelLength={100} items={breadcrumbItems} textSize="base" persistFirstItem />
}
